<template>
  <div class="random-cases-section" v-if="randomCases && randomCases.length">
    <div class="wrapper">
      <div class="slider-header">
        <div class="left">
          <div class="bottom">
            <h2 class="title">{{ $t('IdeasCoins Bundles') }}</h2>
          </div>
        </div>
      </div>

      <div class="random-cases-slider" @click="handleClick">
        <button
            class="nav-arrow prev"
            @click.stop="previous"
            v-show="!atStart"
            aria-label="Previous"
        >
          <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
          >
            <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
          </svg>
        </button>

        <Carousel
            ref="carousel"
            :items-to-show="slidesToShow"
            :wrap-around="false"
            :mouse-drag="true"
            :snap-align="'start'"
            @slideChange="handleSlideChange"
        >
          <Slide
              v-for="(card, index) in randomCases"
              :key="index"
              class="item card"
              :data-slug="card.slug"
          >
            <div class="card-wrapper item-wrapper" :class="card.type">
              <div class="card-content">
                <div class="card-image-container">
                  <img
                      :src="card.image"
                      :alt="card.name"
                      class="card-image"
                      loading="lazy"
                  />
                </div>
                <div class="card-footer">
                  <h3 class="card-title">{{ card.name }}</h3>
                  <p class="card-description">{{ card.description }}</p>
                  <div class="price-container buy">
                    <div class="card-price title extrasmall">
                      {{ card.price }} {{ currencySymbol }}
                    </div>
                    <button class="button hover-link">
                      <img src="./../img/cart.svg" class="img" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Slide>
        </Carousel>

        <button
            class="nav-arrow next"
            @click.stop="next"
            v-show="!atEnd"
            aria-label="Next"
        >
          <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
          >
            <path
                d="M9 18L15 12L9 6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {defineComponent, ref, onMounted, onBeforeUnmount, nextTick, computed, watch} from 'vue';
import {useStore} from 'vuex';
import {Carousel, Slide} from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import axios from "@/axios";
import {useRouter} from "vue-router";

export default defineComponent({
  name: 'RandomCasesSlider',
  components: {Carousel, Slide},
  inject: ['openSignInModal'],
  setup() {
    const store = useStore();
    const router = useRouter()

    const randomCases = ref([]);
    const atStart = ref(true);
    const atEnd = ref(false);
    const carousel = ref(null);

    const sliderIsClickable = ref(true);

    // Динамически рассчитываем сколько слайдов показывать по ширине окна
    const calculateSlidesToShow = () => {
      let containerWidth = window.innerWidth >= 1440 ? 1200 : window.innerWidth - 16;
      const cardWidth = 280;
      const gap = 24;
      return Math.floor((containerWidth + gap) / (cardWidth + gap));
    };

    const slidesToShow = ref(calculateSlidesToShow());

    // Получаем валюту и символ из vuex
    const currencySymbol = computed(() => store.getters['app/currencySymbol']);
    const currency = computed(() => store.getters['app/currency']);

    // Фетчим кейсы
    const fetchRandomCases = async () => {
      try {
        const res = await axios.get('coin-bundles');
        if (res.status === 200) {
          randomCases.value = res.data.payload;
          await nextTick();
          updateNavigationState();
        }
      } catch (error) {
        if (error.response?.status === 401) {
          this.openSignInModal();
        }
      }
    };

    const updateNavigationState = () => {
      if (!carousel.value) return;
      atStart.value = carousel.value.currentSlide === 0;
      atEnd.value =
          carousel.value.currentSlide >= randomCases.value.length - slidesToShow.value;
    };

    const handleSlideChange = () => {
      updateNavigationState();
    };

    const next = () => {
      if (!atEnd.value) {
        carousel.value.next();
      }
    };

    const previous = () => {
      if (!atStart.value) {
        carousel.value.prev();
      }
    };

    const handleClick = (event) => {
      if (!sliderIsClickable.value) return;
      const card = event.target.closest('.card');
      if (card) {
        const slug = card.dataset.slug;
        router.push(`/coin-bundles/${slug}`);
      }
    };

    const handleResize = () => {
      slidesToShow.value = calculateSlidesToShow();
      nextTick(() => {
        if (carousel.value) {
          carousel.value.slideTo(carousel.value.currentSlide);
          updateNavigationState();
        }
      });
    };

    const handleSwipe = () => {
      sliderIsClickable.value = false;
      setTimeout(() => {
        sliderIsClickable.value = true;
      }, 300);
    };

    onMounted(() => {
      console.log('random')
      fetchRandomCases();
      window.addEventListener('resize', handleResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
    });

    // Следим за изменением валюты — обновляем кейсы
    watch(currency, () => {
      fetchRandomCases();
    });

    return {
      randomCases,
      currencySymbol,
      atStart,
      atEnd,
      carousel,
      slidesToShow,
      handleSlideChange,
      next,
      previous,
      handleClick,
      handleSwipe,
    };
  },
});
</script>

<style lang="scss" scoped>
.random-cases-section {
  margin-top: 120px;
  padding: 0 16px;
  overflow: hidden;

  .slider-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 5px;
    line-height: 65px;
    margin-bottom: 40px;

    .left {
      .bottom {
        display: flex;
        gap: 11px;
      }

      .slider-title {
        font-size: calc(clamp(1rem, 0.1429rem + 4.2857vw, 4rem));
        color: #070707;
        font-weight: 700;
      }
    }
  }

  /* vue3-carousel контейнер */
  .carousel {
    position: relative;
    padding: 0 40px;

    &__track {
      display: flex;
      gap: 24px;
    }

    &__slide {
      width: 280px !important;
      flex-shrink: 0;
      cursor: pointer;
      transition: opacity 0.15s ease;

      &:hover {
        opacity: 0.6;
      }
    }
  }

  .nav-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #fff;
    border: 1px solid #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 2;
    transition: all 0.2s ease;
    color: #131313;

    &:hover {
      background: #f5f5f5;
      border-color: #d0d0d0;
    }

    &.prev {
      left: 0;
    }

    &.next {
      right: 0;
    }
  }
}

.card-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 280px;
  position: relative;
  margin: 0 12px;
  cursor: pointer;
}

.card-content {
  display: flex;
  gap: 16px;
  width: 100%;
}

.card-image-container {
  position: relative;
  width: 130px;
  height: 130px;
  pointer-events: none;
  background: #fff;
  border-radius: 24px;
}

.card-footer {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  justify-content: flex-start;
}

.card-image {
  position: relative;
  z-index: 1;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  margin-bottom: 8px;
  color: #131313;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}

.card-description {
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  color: #131313;
}

.random-cases-slider {
  position: relative;
  padding: 0 40px;
}

@media screen and (max-width: 900px) {
  .random-cases-section {
    margin-top: 48px;

    .carousel {
      padding: 0 !important;

      &__slide {
        width: auto !important;
      }
    }

    .nav-arrow {
      width: 32px;
      height: 32px;

      svg {
        width: 20px;
        height: 20px;
      }
    }

    .random-cases-slider {
      padding: 0 36px;
    }
  }
}
</style>
